import C from "@/modules/main/MainLayout.module.css";

import { LayoutContent } from "@/modules/main/LayoutContent.tsx";
import { showOnlyContentAtom } from "@/modules/main/MainLayout.store.ts";
import { SideNav } from "@/modules/main/SideNav.tsx";
import { TopNav } from "@/modules/main/TopNav.tsx";
import { SetSectionFromRouter } from "@/modules/main/sidebar/Sidebar.routing.tsx";
import { closeSidebarAction, currentSectionAtom, sectionsAtom } from "@/modules/main/sidebar/Sidebar.store.tsx";
import { Sidebar } from "@/modules/main/sidebar/Sidebar.tsx";
import type { BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { useAtom, useAtomValue, useSetAtom } from "jotai/react";
import { memo, useTransition, type ReactNode } from "react";

const MainSideNav = memo(function MainSideNav(props: BaseProps) {
    const [sectionId, setSectionId] = useAtom(currentSectionAtom);
    const closeSidebar = useSetAtom(closeSidebarAction);
    const sidebarSections = useAtomValue(sectionsAtom);
    // TODO: display a loading indicator while transitioning?
    const [_inTransition, startTransition] = useTransition();

    return (
        <SideNav
            items={sidebarSections}
            currentItemId={sectionId}
            onPressItem={(section) => {
                // Start a transition - this means any Suspense fallbacks will not be shown (e.g. due to lazy loading in the side bar)
                startTransition(() => {
                    if (section.id === sectionId) {
                        console.debug("Clicked on the same section");
                        closeSidebar();
                    } else {
                        setSectionId(section.id);
                    }
                });
            }}
            {...props}
        />
    );
});

export function MainLayout({ children }: { children?: ReactNode }) {
    const showOnlyContent = useAtomValue(showOnlyContentAtom);
    if (showOnlyContent) {
        return children;
    }

    return (
        <div className="size-full flex flex-col">
            <SetSectionFromRouter />
            <TopNav />
            <div className={cn(C.mainArea)}>
                <MainSideNav className={cn(C.ScrollableNav)} />
                <Sidebar className={cn(C.Sidebar)} />
                <LayoutContent className={cn("flex-grow flex flex-col", C.Scrollable, C.strict)}>
                    {children}
                </LayoutContent>
            </div>
        </div>
    );
}
